import { useEffect, useState } from "react";
import type { FieldErrors, FieldValues, Path, UseFormReturn } from "react-hook-form";

export function useWizardSteps<T extends FieldValues>({
  form,
  stepCount,
  stepFields,
  minStep = 0,
  initialStep = 0,
  initialMaxValidated = 0,
}: {
  form: UseFormReturn<T>;
  stepCount: number;
  stepFields: Partial<Record<number, Path<T>[]>>;
  minStep?: number;
  initialStep?: number;
  initialMaxValidated?: number;
}) {
  const clamp = (n: number) => Math.min(Math.max(n, 0), stepCount - 1);
  const [step, setStep] = useState(() => clamp(initialStep));
  const [maxValidated, setMaxValidated] = useState(() => clamp(initialMaxValidated));

  useEffect(() => {
    const sub = form.watch((_, info) => {
      setMaxValidated((m) => (step <= m ? step - 1 : m));
      const fields = stepFields[step];
      if (!info.name || !fields) return;
      const invalid = fields.filter((f) => form.getFieldState(f).error);
      if (invalid.length > 0) void form.trigger(invalid);
    });
    return () => sub.unsubscribe();
  }, [form, step]);

  function focusFirstError(fields: Path<T>[]) {
    const firstErr = fields.find((f) => form.getFieldState(f).error);
    if (firstErr) form.setFocus(firstErr, { shouldSelect: true });
    requestAnimationFrame(() => {
      document.querySelector('[aria-invalid="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  function goToError(errors: FieldErrors<T>) {
    const steps = Object.keys(stepFields).map(Number).sort((a, b) => a - b);
    const target =
      steps.find((s) => (stepFields[s] ?? []).some((f) => f in errors)) ?? steps[steps.length - 1];
    if (target == null) return;
    setStep(target);
    requestAnimationFrame(() => focusFirstError(stepFields[target] ?? []));
  }

  async function next(): Promise<boolean> {
    const fields = stepFields[step];
    if (fields && !(await form.trigger(fields))) {
      focusFirstError(fields);
      return false;
    }
    setMaxValidated((m) => Math.max(m, step));
    setStep((s) => Math.min(s + 1, stepCount - 1));
    return true;
  }

  function back() {
    setStep((s) => Math.max(s - 1, minStep));
  }

  function goToStep(target: number) {
    if (target <= maxValidated || target === step) setStep(target);
  }

  function reset() {
    setStep(0);
    setMaxValidated(0);
  }

  return { step, setStep, maxValidated, next, back, goToStep, goToError, focusFirstError, reset };
}
